// material-ui
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

// assets
import SearchOutlined from '@ant-design/icons/SearchOutlined';

// api
// import { searchApi } from '../../../../api/search/search.api';

// hooks
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ==============================|| HEADER CONTENT - SEARCH ||============================== //

const getResultTypeColor = (type) => {
  switch (type) {
    case 'order':
      return '#6f4fd4';
    case 'product_catalog':
    return '#f57c00';
    case 'listing':
     
         return '#388e3c';
    case 'fulfilment_product':
      return '#7b1fa2';
    default:
      return '#757575';
  }
};

const getResultTypeLabel = (type) => {
  switch (type) {
    case 'order':
      return 'Order';
    case 'product_catalog':
      return 'Product Catalog';
    case 'listing':
      return 'Listing';
    case 'fulfilment_product':
      return 'Inventory';
    default:
      return 'Unknown';
  }
};

export default function Search() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const response = await searchApi.universalSearch(searchQuery);
      setResults(response.data.results || []);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    // Don't automatically search - only search on button click
    if (!value.trim()) {
      setResults([]);
      setShowResults(false);
    }
  };

  const handleSearchClick = () => {
    if (query.trim()) {
      performSearch(query);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && query.trim()) {
      performSearch(query);
    } else if (event.key === 'Escape') {
      setShowResults(false);
    }
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setQuery('');
    
    switch (result.type) {
      case 'order':
        navigate(`/orders/order-details/${result.salesChannelOrderId}`);
        break;
      case 'product_catalog':
        navigate(`/product-catalog/${result.id}`);
        break;
      case 'listing':
        navigate(`/listing/${result.id}`);
        break;
      case 'fulfilment_product':
        navigate(`/inventory/${result.id}`);
        break;
      default:
        console.warn('Unknown result type:', result.type);
    }
  };

  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
     
return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const renderResultItem = (result) => {
    const primaryText = result.name || result.orderNumber || result.id;
    
    // Build details array for horizontal display
    const getDetails = () => {
      const details = [];
      
      if (result.type === 'order') {
        if (result.customerName) details.push(`Customer: ${result.customerName}`);
        if (result.currency && result.totalPrice) details.push(`${result.currency} ${result.totalPrice}`);
        if (result.financialStatus) details.push(`Status: ${result.financialStatus}`);
      } else if (result.type === 'fulfilment_product') {
        if (result.sku) details.push(`SKU: ${result.sku}`);
        if (result.brand) details.push(`Brand: ${result.brand}`);
        if (result.upc) details.push(`UPC: ${result.upc}`);
        if (result.mpn) details.push(`MPN: ${result.mpn}`);
        if (result.wholesalePrice) details.push(`Wholesale: $${result.wholesalePrice}`);
        if (result.msrp) details.push(`MSRP: $${result.msrp}`);
      } else if (result.type === 'product_catalog') {
        // Extract from ProductCatalogVariant array
        const variant = result.ProductCatalogVariant && result.ProductCatalogVariant[0];
        if (variant?.sku) details.push(`SKU: ${variant.sku}`);
        if (result.brand) details.push(`Brand: ${result.brand}`);
        if (variant?.upc) details.push(`UPC: ${variant.upc}`);
        if (variant?.mpn) details.push(`MPN: ${variant.mpn}`);
         if (variant.msrp) details.push(`MSRP: $${variant.msrp}`);
      } else if (result.type === 'listing') {
        // Extract from ListingVariant array
        const variant = result.ListingVariant && result.ListingVariant[0];
        if (variant?.sku) details.push(`SKU: ${variant.sku}`);
        if (result.brand) details.push(`Brand: ${result.brand}`);
        if (variant?.upc) details.push(`UPC: ${variant.upc}`);
        if (variant?.mpn) details.push(`MPN: ${variant.mpn}`);
        
        if (variant?.listPrice) details.push(`Price: ${result.currency || '$'}${variant.listPrice}`);
        if (result.status) details.push(`Status: ${result.status}`);
      }
      
      return details;
    };
    
    const details = getDetails();
    
     
return (
      <Box
        key={`${result.type}-${result.id}`}
        onClick={() => handleResultClick(result)}
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          cursor: 'pointer',
          backgroundColor: 'white',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: getResultTypeColor(result.type),
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3, flex: 1 }}>
            {primaryText}
          </Typography>
          <Chip
            label={getResultTypeLabel(result.type)}
            size="small"
            sx={{
              backgroundColor: getResultTypeColor(result.type),
              color: 'white',
              fontSize: '0.65rem',
              height: '18px',
              ml: 1,
              flexShrink: 0,
            }}
          />
        </Box>
        
        {/* Display details in rows */}
        {details.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {details.map((detail, index) => (
              <Typography 
                key={index} 
                variant="caption" 
                color="textSecondary" 
                sx={{ 
                  display: 'inline-block',
                  mr: 2,
                  mb: 0.5,
                  fontSize: '0.75rem',
                  lineHeight: 1.2
                }}
              >
                {detail}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

   
return (
    <Box sx={{ width: '100%', ml: { xs: 0, md: 1 }, position: 'relative' }} ref={searchRef}>
      {/* <FormControl sx={{ width: { xs: '100%', md: 224 } }}>
        <OutlinedInput
          size="small"
          id="header-search"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          endAdornment={
            <InputAdornment position="start" sx={{ mr: -0.5 }}>
              <Box 
                onClick={handleSearchClick}
                sx={{ 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <SearchOutlined />
                )}
              </Box>
            </InputAdornment>
          }
          aria-describedby="header-search-text"
          slotProps={{ input: { 'aria-label': 'search' } }}
          placeholder="Search products, orders..."
          sx={{ border: "1px solid #B0B0B0" }}
        />
      </FormControl> */}
      
      {showResults && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '400px',
            maxWidth:500,
            overflowY: 'auto',
            mt: 1,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            p: 1,
          }}
        >
          {results.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {results.map(renderResultItem)}
            </Box>
          ) : query && !loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No results found for "{query}"
              </Typography>
            </Box>
          ) : null}
        </Paper>
      )}
    </Box>
  );
}
